import React, { useMemo, useRef, useState } from "react";

import { Box, Typography } from "@mui/material";
import { CardSizeProvider } from "components/Report-generator/context/CardSizeContext";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import type {
  AICardConfig,
  BenchmarkComparisonCardData,
  CanvasCard,
  CardConfig,
  CardType,
  CashFlowTimelineCardData,
  Chart,
  DataFreshnessCardData,
  DataTableCardData,
  ExposureCardData,
  KPICardData,
  MacroBriefCardConfig,
  MacroBriefCardData,
  PerformanceAttributionCardData,
  ReportOrientation,
  SizePreset,
  SummaryCardData,
  TableCardConfig,
  UnfundedCardData
} from "store/types";
import { DEFAULT_GRID_H, GRID_CONSTANTS, PRESET_GRID_W } from "store/types/reports/report-generator";

import { gridWToWidthPercent } from "../utils/gridUtils";
import { countKpiMetrics, KPI_EDIT_CHROME_PX, kpiMetricsFit } from "../utils/kpiLayout";

import { AICard } from "./cards/AICard";
import { BenchmarkComparisonCard } from "./cards/BenchmarkComparisonCard";
import { CashFlowTimelineCard } from "./cards/CashFlowTimelineCard";
import { ChartCard } from "./cards/ChartCard";
import { DataFreshnessCard } from "./cards/DataFreshnessCard";
import { DataTableCard } from "./cards/DataTableCard";
import { ExposureBreakdownCard } from "./cards/ExposureBreakdownCard";
import { KpiCard } from "./cards/KpiCard";
import { MacroBriefCard } from "./cards/MacroBriefCard";
import { PerformanceAttributionCard } from "./cards/PerformanceAttributionCard";
import { SummaryCard } from "./cards/SummaryCard";
import { TextCard } from "./cards/TextCard";
import { UnfundedCommitmentsCard } from "./cards/UnfundedCommitmentsCard";
import { CardTimeRangeChip } from "./CardTimeRangeChip";

const CARD_LABELS: Record<CardType, string> = {
  kpi: "KPI",
  summary: "Summary",
  chart: "Chart",
  table: "Data Table",
  text: "Text",
  ai_card: "AI Card",
  exposure: "Exposure",
  cashflow: "Cash Flow",
  unfunded: "Unfunded",
  performance: "Performance",
  benchmark: "Benchmark",
  dataFreshness: "Freshness",
  macroBrief: "Macro Brief"
};

interface Props {
  card: CanvasCard;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onOpenSettings: () => void;
  onUpdateConfig: (config: CardConfig) => void;
  onPresetChange: (preset: SizePreset) => void;
  orientation: ReportOrientation;
  readOnly?: boolean;
}

function activePreset(gridW: number): SizePreset | null {
  if (gridW === PRESET_GRID_W.S) return "S";
  if (gridW === PRESET_GRID_W.M) return "M";
  if (gridW === PRESET_GRID_W.F) return "F";
  return null;
}

export const CardWrapper: React.FC<Props> = ({
  card,
  isSelected,
  onSelect,
  onRemove,
  onDuplicate,
  onOpenSettings,
  onUpdateConfig,
  onPresetChange,
  orientation,
  readOnly
}) => {
  const [hovered, setHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setHovered(true);
  };
  const handleMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => setHovered(false), 100);
  };

  const currentPreset = activePreset(card.gridW);

  // For KPI cards, forbid shrinking to a preset that would clip metrics out of
  // the (fixed-height) section.
  const kpiMetricCount = card.config.type === "kpi" ? countKpiMetrics(card) : 0;
  const isPresetDisabled = (preset: SizePreset) => {
    if (kpiMetricCount === 0) return false;
    const widthPercent = gridWToWidthPercent(PRESET_GRID_W[preset]);
    const heightPx = DEFAULT_GRID_H.kpi * GRID_CONSTANTS.GRID_CELL_HEIGHT;
    return !kpiMetricsFit(kpiMetricCount, widthPercent, heightPx, KPI_EDIT_CHROME_PX);
  };

  const cardSizeCtx = useMemo(
    () => ({
      widthPercent: card.widthPercent,
      heightPx: card.heightPx,
      orientation,
      isEditMode: true
    }),
    [card.widthPercent, card.heightPx, orientation]
  );

  const showControls = (hovered || isSelected) && !readOnly;

  return (
    <Box
      ref={cardRef}
      sx={{
        width: "100%",
        height: "100%",
        position: "relative"
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onOpenSettings();
      }}
    >
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderRadius: "6px",
          boxShadow: isSelected
            ? "0 0 0 2px #1A3C34, 0 4px 16px rgba(0,0,0,0.10)"
            : "0 1px 3px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)",
          cursor: readOnly ? "pointer" : "default",
          position: "relative",
          height: "100%",
          userSelect: "none",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* Drag handle bar — always present when editable */}
        {!readOnly && (
          <Box sx={{ position: "relative", flexShrink: 0, height: 28 }}>
            <Box
              className="rgl-drag-handle"
              sx={{
                display: "flex",
                alignItems: "center",
                px: "4px",
                height: 28,
                cursor: "grab",
                background: showControls
                  ? "linear-gradient(to bottom, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)"
                  : "transparent",
                borderBottom: showControls ? "1px solid rgba(7,31,53,0.08)" : "none",
                transition: "background 150ms ease-out"
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  p: "2px 8px",
                  bgcolor: showControls ? "#1A3C34" : "rgba(7,31,53,0.06)",
                  borderRadius: "4px",
                  userSelect: "none",
                  transition: "background 150ms ease-out"
                }}
              >
                <GripIcon active={showControls} />
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: showControls ? "#FFFFFF" : "rgba(7,31,53,0.35)",
                    transition: "color 150ms ease-out"
                  }}
                >
                  {CARD_LABELS[card.type]}
                </Typography>
              </Box>

              <CardTimeRangeChip config={card.config} sx={{ ml: "6px" }} />
            </Box>

            <Box
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              sx={{
                position: "absolute",
                top: 0,
                right: 4,
                height: 28,
                display: "flex",
                alignItems: "center",
                gap: "3px",
                zIndex: 1,
                opacity: showControls ? 1 : 0,
                pointerEvents: showControls ? "auto" : "none",
                transition: "opacity 150ms ease-out"
              }}
            >
              {(["S", "M", "F"] as SizePreset[]).map((p) => {
                const disabled = isPresetDisabled(p);
                return (
                  <MaterialTooltip
                    key={p}
                    isShow
                    variant="dark"
                    title={
                      disabled
                        ? "Too small \u2014 metrics wouldn't fit. Remove a metric first."
                        : card.type === "table"
                          ? p === "S"
                            ? "Small \u2014 ~3 columns"
                            : p === "M"
                              ? "Medium \u2014 ~6 columns"
                              : "Full \u2014 all columns"
                          : p === "S"
                            ? "Small (\u2153 width)"
                            : p === "M"
                              ? "Medium (\u00BD width)"
                              : "Full width"
                    }
                  >
                    <Box
                      component="button"
                      disabled={disabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (disabled) return;
                        onPresetChange(p);
                      }}
                      sx={{
                        width: 22,
                        height: 22,
                        border: "none",
                        borderRadius: "3px",
                        bgcolor: currentPreset === p ? "#1A3C34" : "#E5E7EB",
                        color: currentPreset === p ? "#FFFFFF" : "#6B7280",
                        fontSize: 10,
                        fontWeight: 700,
                        cursor: disabled ? "not-allowed" : "pointer",
                        opacity: disabled ? 0.4 : 1,
                        transition: "background 120ms ease-out"
                      }}
                    >
                      {p}
                    </Box>
                  </MaterialTooltip>
                );
              })}
              <MaterialTooltip variant="dark" isShow title="Card settings">
                <Box
                  component="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenSettings();
                  }}
                  sx={{
                    width: 22,
                    height: 22,
                    border: "none",
                    borderRadius: "3px",
                    bgcolor: "#071f35",
                    color: "#FFFFFF",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <TuneIcon />
                </Box>
              </MaterialTooltip>
              <MaterialTooltip variant="dark" isShow title="Duplicate card">
                <Box
                  component="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate();
                  }}
                  sx={{
                    width: 22,
                    height: 22,
                    border: "none",
                    borderRadius: "3px",
                    bgcolor: "#E8F5F0",
                    color: "#1A3C34",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  {"\u29C9"}
                </Box>
              </MaterialTooltip>
              <MaterialTooltip variant="dark" isShow title="Remove card">
                <Box
                  component="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                  }}
                  sx={{
                    width: 22,
                    height: 22,
                    border: "none",
                    borderRadius: "3px",
                    bgcolor: "#FEE2E2",
                    color: "#DC2626",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  {"\u2715"}
                </Box>
              </MaterialTooltip>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowX: "hidden",
            overflowY: "hidden",
            borderRadius: readOnly ? "6px" : "0 0 6px 6px"
          }}
        >
          <CardSizeProvider value={cardSizeCtx}>
            <CardContent
              card={card}
              onUpdateConfig={onUpdateConfig}
              onTransformToAI={() => {
                onUpdateConfig({ type: "ai_card", question: "", answer: "" });
                onSelect();
              }}
            />
          </CardSizeProvider>
        </Box>
      </Box>
    </Box>
  );
};

interface CardContentProps {
  card: CanvasCard;
  onUpdateConfig: (config: CardConfig) => void;
  onTransformToAI: () => void;
}

const CardContent: React.FC<CardContentProps> = ({ card, onUpdateConfig, onTransformToAI }) => {
  return (
    <>
      {card.config.type === "kpi" && <KpiCard data={card.data as KPICardData | undefined} loading={card.loading} />}
      {card.config.type === "summary" && (
        <SummaryCard data={card.data as SummaryCardData | undefined} loading={card.loading} />
      )}
      {card.config.type === "chart" && <ChartCard data={card.data as Chart | undefined} loading={card.loading} />}
      {card.config.type === "table" && (
        <DataTableCard
          data={card.data as DataTableCardData | undefined}
          loading={card.loading}
          columnSettings={(card.config as TableCardConfig).columns}
          columnWidths={(card.config as TableCardConfig).columnWidths}
          onColumnWidthsChange={(columnWidths) => onUpdateConfig({ ...(card.config as TableCardConfig), columnWidths })}
        />
      )}
      {card.config.type === "text" && (
        <TextCard
          config={card.config}
          onChange={(content) => onUpdateConfig({ type: "text", content })}
          onTransformToAI={onTransformToAI}
        />
      )}
      {card.config.type === "ai_card" && (
        <AICard
          config={card.config as AICardConfig}
          data={card.data}
          loading={card.loading}
          onChange={(answer) => onUpdateConfig({ ...(card.config as AICardConfig), answer })}
          onAsk={(question) => onUpdateConfig({ ...(card.config as AICardConfig), question, answer: undefined })}
          onTransformToText={() => onUpdateConfig({ type: "text", content: "" })}
        />
      )}
      {card.config.type === "exposure" && (
        <ExposureBreakdownCard
          config={card.config}
          data={card.data as ExposureCardData | undefined}
          loading={card.loading}
        />
      )}
      {card.config.type === "cashflow" && (
        <CashFlowTimelineCard data={card.data as CashFlowTimelineCardData[] | undefined} loading={card.loading} />
      )}
      {card.config.type === "unfunded" && (
        <UnfundedCommitmentsCard data={card.data as UnfundedCardData[] | undefined} loading={card.loading} />
      )}
      {card.config.type === "performance" && (
        <PerformanceAttributionCard
          config={card.config}
          data={card.data as PerformanceAttributionCardData | undefined}
          loading={card.loading}
        />
      )}
      {card.config.type === "benchmark" && (
        <BenchmarkComparisonCard
          config={card.config}
          data={card.data as BenchmarkComparisonCardData | undefined}
          loading={card.loading}
        />
      )}
      {card.config.type === "dataFreshness" && (
        <DataFreshnessCard data={card.data as DataFreshnessCardData | undefined} loading={card.loading} />
      )}
      {card.config.type === "macroBrief" && (
        <MacroBriefCard
          data={card.data as MacroBriefCardData | undefined}
          config={card.config as MacroBriefCardConfig}
          loading={card.loading}
        />
      )}
    </>
  );
};

const TuneIcon: React.FC = () => {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
      <circle cx="8" cy="6" r="2" fill="currentColor" />
      <circle cx="16" cy="12" r="2" fill="currentColor" />
      <circle cx="10" cy="18" r="2" fill="currentColor" />
    </svg>
  );
};

const GripIcon: React.FC<{ active?: boolean }> = ({ active = true }) => {
  return (
    <svg width="10" height="12" viewBox="0 0 10 12" fill="none">
      {[0, 1].map((col) =>
        [0, 1, 2].map((row) => (
          <circle
            key={`${col}-${row}`}
            cx={col * 5 + 2}
            cy={row * 4 + 2}
            r={1.3}
            fill={active ? "rgba(255,255,255,0.8)" : "rgba(7,31,53,0.25)"}
          />
        ))
      )}
    </svg>
  );
};
